import Colors from "./colors";
import Division from "./division";
import DivisionSeason from "./divisionSeason";
import Franchise from "./franchise";
import Game from "./game";
import GameCollection from "./gameCollection";
import GameTeam from "./gameTeam";
import League from "./league";
import LeagueSeason from "./leagueSeason";
import Message from "./message";
import MessageCollection from "./messageCollection";
import Role from "./role";
import Team from "./team";
import TeamCollection from "./teamCollection";
import TeamLogo from "./teamLogo";
import TeamLogoCollection from "./teamLogoCollection";
import User from "./user";
import UserCollection from "./userCollection";
import UserInfo from "./userInfo";

class Factory {
  /**
   *
   * @param {object} userInfoData
   */
  constructor(userInfoData) {
    this.userInfo = null;
    if (userInfoData) {
      this.makeUserInfo(userInfoData);
    }
  }

  /**
   *
   * @param {object} colors
   * @returns {Colors}
   */
  makeColors(colors) {
    return new Colors(colors);
  }

  /**
   *
   * @param {object} divisionObj
   * @returns {Division}
   */
  makeDivision(divisionObj) {
    return new Division(divisionObj, this.userInfo);
  }

  /**
   *
   * @param {object} divisionSeasonObj
   * @returns {DivisionSeason}
   */
  makeDivisionSeason(divisionSeasonObj) {
    return new DivisionSeason(divisionSeasonObj, this.userInfo);
  }

  /**
   *
   * @param franchiseObj
   * @returns {Franchise}
   */
  makeFranchise(franchiseObj) {
    return new Franchise(franchiseObj, this.userInfo);
  }

  /**
   *
   * @param {object} gameObj
   * @param {TeamCollection} leagueSeasonTeams
   * @returns {Game}
   */
  makeGame(gameObj, leagueSeasonTeams) {
    const game = new Game(gameObj, this.userInfo);

    const loaded =
      gameObj && leagueSeasonTeams && leagueSeasonTeams.hasLoaded();
    if (loaded) {
      if (gameObj.away)
        game.away = this.makeGameTeam(gameObj.away, leagueSeasonTeams);
      if (gameObj.home)
        game.home = this.makeGameTeam(gameObj.home, leagueSeasonTeams);
    }
    game.setLabel();

    return game;
  }

  makeGameCollection(gamesObj, leagueSeasonTeams) {
    const loaded =
      gamesObj && leagueSeasonTeams && leagueSeasonTeams.hasLoaded();
    const games = {};
    if (loaded) {
      Object.keys(gamesObj).forEach((week) => {
        let weekGames = gamesObj[week];
        games[week] = [];
        Object.keys(weekGames).forEach((gameId) => {
          let gameObj = gamesObj[week][gameId];
          let game = this.makeGame(gameObj, leagueSeasonTeams);
          games[week].push(game);
        });
      });
    }

    return new GameCollection(games);
  }

  /**
   *
   * @param {object} gameTeamObj
   * @param {TeamCollection} leagueSeasonTeams
   * @returns {GameTeam}
   */
  makeGameTeam(gameTeamObj, leagueSeasonTeams) {
    // get team
    let loaded =
      gameTeamObj && leagueSeasonTeams && leagueSeasonTeams.hasLoaded();
    let hasTeam = !!gameTeamObj.team;

    let team = null;
    if (loaded && hasTeam)
      team = leagueSeasonTeams.getTeamById(gameTeamObj.team);

    return new GameTeam(gameTeamObj, this.userInfo, team);
  }

  /**
   *
   * @param {object} leagueObj
   * @returns {League}
   */
  makeLeague(leagueObj) {
    return new League(leagueObj, this.userInfo);
  }

  /**
   *
   * @param {object} leagueSeasonObj
   * @returns {LeagueSeason}
   */
  makeLeagueSeason(leagueSeasonObj) {
    return new LeagueSeason(leagueSeasonObj, this.userInfo);
  }

  /**
   *
   * @param messageObj
   * @returns {Message}
   */
  makeMessage(messageObj) {
    return new Message(messageObj, this.userInfo);
  }

  /**
   *
   * @param messageObjs
   * @param replyObjs
   * @returns {MessageCollection}
   */
  makeMessageCollection(messageObjs, replyObjs) {
    const messages = Object.keys(messageObjs).map((id) =>
      this.makeMessage(messageObjs[id])
    );
    const replies = Object.keys(replyObjs).map((id) =>
      this.makeMessage(replyObjs[id])
    );

    return new MessageCollection(messages, replies);
  }

  /**
   *
   * @param {object} roleObj
   * @param {string} username
   * @returns {Role}
   */
  makeRole(roleObj, username) {
    return new Role(roleObj, username);
  }

  /**
   *
   * @param {object} teamObj
   * @returns {Team}
   */
  makeTeam(teamObj) {
    return new Team(teamObj, this.userInfo);
  }

  /**
   *
   * @param {object} teamsObj
   * @returns {TeamCollection}
   */
  makeTeamCollection(teamsObj) {
    const teams = Object.keys(teamsObj).map((teamId) =>
      this.makeTeam(teamsObj[teamId])
    );

    return new TeamCollection(teams);
  }

  /**
   *
   * @param {object} usersObj
   * @returns {UserCollection}
   */
  makeUserCollection(usersObj) {
    const users = Object.keys(usersObj).map((username) =>
      this.makeUser(usersObj[username])
    );

    return new UserCollection(users);
  }

  /**
   *
   * @param {object} teamLogoObj
   * @param {Team} team
   * @returns {TeamLogo}
   */
  makeTeamLogo(teamLogoObj, team) {
    return new TeamLogo(teamLogoObj, team);
  }

  /**
   *
   * @param {Team} team
   * @returns {TeamLogoCollection}
   */
  makeTeamLogoCollection(team) {
    const teamLogos = Object.keys(team.obj.logos).map((id) =>
      this.makeTeamLogo(team.obj.logos[id], team)
    );

    return new TeamLogoCollection(teamLogos);
  }

  /**
   *
   * @param {object} userObj
   * @returns {User}
   */
  makeUser(userObj) {
    return new User(userObj, this.userInfo);
  }

  /**
   *
   * @param {object} userInfoData
   * @returns {UserInfo}
   */
  makeUserInfo(userInfoData) {
    this.userInfo = new UserInfo(userInfoData);
    return this.userInfo;
  }
}

export default Factory;
